import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Alert, Box, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { QuotaBalanceDto } from '@/types/dtos/entitlements/quota-balance.dto';
import type { SubscriptionPlan } from '@/types/dtos/transactions/subscription-plan.dto';
import type { OrganizationSubscriptionInstanceDto } from '@/types/dtos/organizations/organization-subscription-instance.dto';
import {
  collectQuotaKeysFromPlan,
  computeQuotaFloor,
  formatQuotaDowngradeProceedAlert,
  formatQuotaStepperCountLine,
  formatQuotaStepperDeltaVsBooked,
  getOveragePricePerUnit,
  getOverageStepForKey,
  getQuotaDisplayTotal,
  includedQuantityForKey,
  maxQuantityForKey,
} from '@/helpers/organization-subscription-pricing';

/**
 * Per-plan quota row: one stepper where the **shared desired total** is mirrored across all tier cards
 * via `onSetLocalExtra` and re-clamped per card to `[included, max]`. The active subscription card now
 * also uses the same local preview path (no immediate add-on purchase from this control); committing the
 * change goes through the cart / checkout flow.
 */
export function PlanCardQuotaSteppers({
  plan,
  subscription,
  balances,
  balancesLoading,
  isActivePlan,
  localQuotaExtra,
  onSetLocalExtra,
  compact = false,
  billingPeriodEndDisplay = null,
  showDeltaVsBooked = true,
  quotaKeys,
  stepByQuotaKey,
  minTotalByQuotaKey,
  tooltipContext = 'organization',
  getBookedTotalForDelta,
}: {
  plan: SubscriptionPlan;
  /** Reserved for callers that need the org id to compose deep-links etc.; this component does not call any APIs directly. */
  organizationId?: string;
  subscription: OrganizationSubscriptionInstanceDto | undefined;
  balances: QuotaBalanceDto[] | undefined;
  balancesLoading: boolean;
  isActivePlan: boolean;
  /** Extra units above `included` for **this card** (preview-only; identical for active and non-active cards). */
  localQuotaExtra: Record<string, number>;
  onSetLocalExtra: (quotaKey: string, nextExtra: number) => void;
  /** Smaller +/- controls (checkout + manage plan). */
  compact?: boolean;
  /** End of the current paid period — downgrade copy references this date. */
  billingPeriodEndDisplay?: string | null;
  /** When false, hide the “Change: ±N …” line under the count (e.g. event first-choose). Default true. */
  showDeltaVsBooked?: boolean;
  /** Limit which quota keys render steppers (default: all keys on the plan). */
  quotaKeys?: readonly string[];
  /**
   * Optional override for +/- click size. Prefer catalog `overagePrices[].step`
   * (used when this is omitted).
   */
  stepByQuotaKey?: Record<string, number>;
  /** Hard minimum selectable **total** per quota key (e.g. playout hours covering flow length). */
  minTotalByQuotaKey?: Record<string, number>;
  /** Event manage-plan uses one-time / flow-length copy instead of org renewal tooltips. */
  tooltipContext?: 'organization' | 'event';
  /**
   * Override booked baseline for the “Change: ±N” subline (e.g. Event: purchased plan+add-ons,
   * never entitlement balances that may include admin grants).
   */
  getBookedTotalForDelta?: (quotaKey: string) => number;
}) {
  const t = useTranslations();
  const isEvent = tooltipContext === 'event';

  const keys = (quotaKeys?.length ? [...quotaKeys] : collectQuotaKeysFromPlan(plan)).filter((k) =>
    collectQuotaKeysFromPlan(plan).includes(k),
  );
  if (!keys.length) {
    return null;
  }

  const balanceFor = (quotaKey: string) => balances?.find((b) => b.quotaKey === quotaKey);

  const btnSx = compact
    ? {
        width: 28,
        height: 28,
        p: 0.25,
        minWidth: 28,
      }
    : undefined;
  const countVariant = compact ? ('caption' as const) : ('body2' as const);
  const stackSpacing = compact ? 0.5 : 1.25;
  /** Match checkout quota block: cap width, stay left when plan cards stack in a column. */
  const compactColumnSx = compact
    ? { maxWidth: 300, alignSelf: 'flex-start' as const, minWidth: 0, width: '100%' }
    : undefined;
  const compactRowSx = compact ? { width: '100%', minWidth: 0 } : undefined;

  if (isActivePlan && balancesLoading) {
    return (
      <Stack spacing={1} sx={compactColumnSx}>
        {keys.map((k) => (
          <Skeleton key={k} variant="rounded" height={compact ? 32 : 40} sx={compact ? { width: '100%' } : undefined} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={stackSpacing} sx={compactColumnSx}>
      {keys.map((quotaKey) => {
        const included = includedQuantityForKey(plan, quotaKey);
        const balance = balanceFor(quotaKey);
        const extraLocal = localQuotaExtra[quotaKey] ?? 0;
        const canPurchaseMore = getOveragePricePerUnit(plan, quotaKey) !== undefined;
        const maxTotal = maxQuantityForKey(plan, quotaKey);

        const bookedPlan = subscription?.subscriptionPlan as SubscriptionPlan | undefined;
        const bookedTotal =
          getBookedTotalForDelta != null
            ? getBookedTotalForDelta(quotaKey)
            : bookedPlan != null
              ? getQuotaDisplayTotal(bookedPlan, quotaKey, balance, subscription?.additionalQuotaUnits)
              : 0;

        /*
         * All cards use the same source of truth: `included(card) + extraLocal`. For the active card,
         * `extraLocal` is seeded by the dialog from the booked `additionalQuotaUnits` so the displayed
         * total matches the current subscription. The active card's floor is the consumed-units floor
         * (granted minus available) so already-assigned seats can never be released by the stepper.
         */
        const step = Math.max(1, stepByQuotaKey?.[quotaKey] ?? getOverageStepForKey(plan, quotaKey));
        const total = included + extraLocal;
        const baseFloor = isActivePlan ? computeQuotaFloor(plan, quotaKey, balance) : included;
        const externalMin = minTotalByQuotaKey?.[quotaKey];
        const floor = Math.max(baseFloor, typeof externalMin === 'number' && externalMin > 0 ? externalMin : 0);
        const minExtra = Math.max(0, floor - included);
        const canDecrease = total > floor;
        const minusDisabled = !canDecrease;
        const floorFromFlowLength =
          typeof externalMin === 'number' && externalMin > 0 && floor === externalMin && quotaKey === 'playout_hours';
        let minusTooltip: string;
        if (isEvent) {
          if (canDecrease) {
            minusTooltip = t('event.plan.quotaStepperDecrease');
          } else if (floorFromFlowLength) {
            minusTooltip = t('event.plan.quotaStepperAtMinPlayoutFlow', { min: floor });
          } else {
            minusTooltip = t('event.plan.quotaStepperAtMinIncluded', { min: floor });
          }
        } else {
          minusTooltip = canDecrease
            ? isActivePlan
              ? t('subscription.organization.planPage.quotaStepperDecreaseHint', { min: floor })
              : t('subscription.organization.planPage.quotaStepperPreviewDecrease')
            : t('subscription.organization.planPage.quotaStepperPreviewAtMin', { min: floor });
        }
        const atPlanMax = maxTotal !== undefined && total >= maxTotal;
        const plusDisabled = !canPurchaseMore || atPlanMax;
        let plusTooltip: string;
        if (isEvent) {
          plusTooltip = atPlanMax
            ? t('event.plan.quotaStepperAtPlanMax')
            : canPurchaseMore
              ? t('event.plan.quotaStepperIncrease')
              : t('event.plan.quotaStepperNoOverage');
        } else {
          plusTooltip = atPlanMax
            ? t('subscription.organization.planPage.quotaStepperAtPlanMax')
            : canPurchaseMore
              ? isActivePlan
                ? t('subscription.organization.planPage.quotaStepperIncreaseHint')
                : t('subscription.organization.planPage.quotaStepperPreviewIncrease')
              : t('subscription.organization.planPage.quotaStepperNoOverage');
        }
        const onMinus = canDecrease
          ? () => onSetLocalExtra(quotaKey, Math.max(minExtra, extraLocal - step))
          : undefined;
        const onPlus = canPurchaseMore && !atPlanMax ? () => onSetLocalExtra(quotaKey, extraLocal + step) : undefined;
        const decreaseAria = isEvent
          ? t('event.plan.quotaDecreaseAria')
          : t('subscription.organization.planPage.quotaDecreaseAria');
        const increaseAria = isEvent
          ? t('event.plan.quotaIncreaseAria')
          : t('subscription.organization.planPage.quotaIncreaseAria');

        const showDowngradeInfo = Boolean(billingPeriodEndDisplay) && subscription != null && bookedTotal > total;

        return (
          <Stack key={quotaKey} spacing={0.75} sx={{ ...compactRowSx, width: '100%', minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={compact ? 0.5 : 1}
              sx={compactRowSx}
            >
              <Tooltip title={minusTooltip}>
                <span>
                  <IconButton
                    type="button"
                    size="small"
                    disabled={minusDisabled}
                    onClick={onMinus}
                    aria-label={decreaseAria}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      color: minusDisabled ? 'action.disabled' : 'text.secondary',
                      ...btnSx,
                    }}
                  >
                    <RemoveIcon sx={{ fontSize: compact ? 16 : 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                <Typography variant={countVariant} fontWeight={600} component="span" display="block">
                  {formatQuotaStepperCountLine(t, quotaKey, total)}
                </Typography>
                {showDeltaVsBooked ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="span"
                    display="block"
                    sx={{ fontSize: compact ? '0.65rem' : undefined }}
                  >
                    {formatQuotaStepperDeltaVsBooked(t, quotaKey, total, bookedTotal)}
                  </Typography>
                ) : null}
              </Box>
              <Tooltip title={plusTooltip}>
                <span>
                  <IconButton
                    type="button"
                    size="small"
                    color={plusDisabled ? 'default' : 'primary'}
                    disabled={plusDisabled}
                    onClick={onPlus}
                    aria-label={increaseAria}
                    sx={{
                      border: 1,
                      borderColor: plusDisabled ? 'divider' : 'primary.main',
                      color: plusDisabled ? 'action.disabled' : undefined,
                      ...btnSx,
                    }}
                  >
                    <AddIcon sx={{ fontSize: compact ? 16 : 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {showDowngradeInfo && billingPeriodEndDisplay ? (
              <Alert severity="info" variant="outlined" sx={{ py: 0.5, fontSize: compact ? '0.75rem' : undefined }}>
                {formatQuotaDowngradeProceedAlert(t, quotaKey, bookedTotal - total, billingPeriodEndDisplay)}
              </Alert>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}
